import React from "react";
import {
  AbsoluteFill,
  Sequence,
  Img,
  OffthreadVideo, 
  Audio,
  useCurrentFrame,
  interpolate,
  spring,
  useVideoConfig,
  Easing,
} from "remotion";
import { TeaserCarousel } from "../../../Common/TeaserCarousel";
import { KaraokeCaptions } from "../../../Common/KaraokeCaptions";

export const IntroSlide = ({
  item,
  theme,
  brand,
  teaserImages = [],
  backgroundVideo,
  ENABLE_AUDIO = true,
}) => {
  const { fps } = useVideoConfig();
  const frame = useCurrentFrame();

  const visualDelay = 20;
  const brandingEnter = spring({ frame: frame - visualDelay, fps, config: { damping: 20, stiffness: 100 } });
  
  const audioDelay = item.audioDelay || 0;
  const karaokeEnter = spring({ frame: frame - audioDelay, fps, config: { damping: 20, stiffness: 100 } });

  const videoBlur = interpolate(brandingEnter, [0, 1], [0, 3]);
  const videoBrightness = interpolate(brandingEnter, [0, 1], [1, 0.6]);

  const exitScale = interpolate(
    frame,
    [item.durationInFrames - 15, item.durationInFrames],
    [1, 15],
    { extrapolateLeft: "clamp", easing: Easing.in(Easing.poly(3)) }
  );
  const pan = interpolate(frame, [0, 150], [0, -20]);

  const videoSrc = backgroundVideo || "https://www.pexels.com/download/video/6003440/";

  return (
    <AbsoluteFill style={{ backgroundColor: "#0f172a" }}>
      {ENABLE_AUDIO && item.props?.audioUrl && (
        <Sequence from={item.audioDelay || 0}>
          <Audio src={item.props.audioUrl} />
        </Sequence>
      )}

      {/* BACKGROUND LAYER */}
      <AbsoluteFill style={{ zIndex: 0 }}>
        <OffthreadVideo 
          src={videoSrc}
          muted={true} 
          style={{
            width: "100%",
            height: "120%",
            objectFit: "cover",
            transform: `translateY(${pan}px)`,
            filter: `brightness(${videoBrightness}) blur(${videoBlur}px)`,
          }}
        />
      </AbsoluteFill>

      {/* EDITORIAL OVERLAY (Gradient fades bottom-up to ground the carousel) */}
      <AbsoluteFill style={{
        background: `linear-gradient(180deg, ${theme.accent} 0%, ${theme.accent} 30%, transparent 60%, ${theme.accent} 100%)`,
        opacity: 0.85,
        zIndex: 0
      }} />

      {/* MAIN CONTENT (Column Stack) */}
      <AbsoluteFill
        style={{
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          padding: "80px 60px",
        }}
      >
        {/* TOP BLOCK: TYPOGRAPHY */}
        <div style={{
          flex: 1.2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          paddingTop: 80, 
        }}>

          <div style={{ marginBottom: 20, opacity: brandingEnter, flexShrink: 0, willChange: "transform, opacity" }}>
            <Img
              src="https://firebasestorage.googleapis.com/v0/b/freelancer-ideas-portal.firebasestorage.app/o/adaptation_engine%2Fbrand_assets%2Floveinc.png?alt=media&token=22866cd2-1b3b-4c74-9e24-762e6cfd2c33"
              style={{ height: 100, width: 'auto' }} // Scaled down for mobile
            />
          </div>

          <div
            style={{
              opacity: brandingEnter,
              transform: `translateY(${(1 - brandingEnter) * 20}px)`,
              backgroundColor: theme.primary,
              color: "white",
              padding: "6px 16px",
              borderRadius: "8px",
              fontSize: 24,
              fontWeight: 700,
              fontFamily: theme.fontMain,
              marginBottom: 20,
              letterSpacing: 2,
              display: 'inline-block',
              flexShrink: 0,
              willChange: "transform, opacity" 
            }}
          >
            THE EDIT
          </div>

          <h1
            style={{
              opacity: brandingEnter,
              transform: `translateY(${(1 - brandingEnter) * 40}px)`,
              fontFamily: theme.fontDisplay || theme.fontMain,
              color: "white",
              fontSize: 110, // Scaled for mobile
              lineHeight: 0.9,
              margin: 0,
              marginBottom: 30,
              textTransform: "uppercase",
              textAlign: "left",
              textShadow: "0 4px 20px rgba(0,0,0,0.6)",
              flexShrink: 0,
              willChange: "transform, opacity" 
            }}
          >
            {item.text || "TITLE HERE"}
          </h1>

          <div style={{
            borderTop: `2px solid ${theme.secondary}`,
            paddingTop: 20,
            width: '100%',
            opacity: brandingEnter,
            transform: `translateY(${(1 - brandingEnter) * 20}px)`,
            flexShrink: 0,
            willChange: "transform, opacity"
          }}>
            <h3
              style={{
                opacity: brandingEnter,
                transform: `translateY(${(1 - brandingEnter) * 60}px)`,
                color: theme.secondary,
                fontFamily: theme.fontMain,
                fontWeight: 400,
                letterSpacing: 4,
                margin: 0,
                fontSize: 28,
                willChange: "transform, opacity"
              }}
            >
              LOVE{brand ? brand.toUpperCase() : "MONEY"}
            </h3>
          </div>

          {/* KARAOKE INJECTION */}
          {item.captions && item.captions.length > 0 && (
            <div
              style={{
                opacity: karaokeEnter,
                transform: `translateY(${(1 - karaokeEnter) * 60}px)`,
                marginTop: 35,
                width: '100%',
                padding: "10px 0px",
                display: "flex",
                alignItems: "center",
                //alignItems: "flex-start", 
                justifyContent: "center", // <--- LIFTED: Centers the flex container horizontally
                flexGrow: 1,
                flexShrink: 0,
                willChange: "transform, opacity" 
              }}
            >
              <div style={{ fontSize: 75, fontFamily: theme.fontMain, textAlign: "center", width: "100%" }}>
                <KaraokeCaptions
                  captions={item.captions}
                  theme={theme}
                  delay={item.audioDelay || 0}
                  align="center"
                />
              </div>
            </div>
          )}
        </div>

        {/* BOTTOM BLOCK: TEASER CAROUSEL */}
        <div style={{
          flex: 0.8,
          position: "relative",
          width: "100%",
          zIndex: exitScale > 1.1 ? 100 : 1,
          transform: `translateY(${(1 - karaokeEnter) * 50}px) scale(${exitScale})`,
          opacity: karaokeEnter,
          willChange: "transform, opacity" 
        }}>
          <TeaserCarousel
            images={teaserImages}
            duration={item.durationInFrames}
            theme={theme}
            delay={audioDelay}
          />
        </div>

      </AbsoluteFill>
    </AbsoluteFill>
  );
};